import React from "react";
import { useI18n } from "@/i18n";
import NewsletterForm from "@/components/site/NewsletterForm";
import { Building2, LineChart, MapPin, ShieldCheck, Users, Layers } from "lucide-react";

const projectIcons = [Building2, Layers, Users, LineChart, MapPin, ShieldCheck];
const valueIcons = [LineChart, Layers, Users, ShieldCheck];

export default function Home() {
  const { t } = useI18n();
  const introParagraphs = t("intro.paragraphs", []);
  const howSteps = t("how.steps", []);
  const howParagraphs = t("how.paragraphs", []);
  const projectCards = t("projects.cards", []);
  const valueFeatures = t("value.features", []);
  const transparencyParagraphs = t("transparency.paragraphs", []);
  const faqItems = t("faq.items", []);

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7 space-y-8">
              <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
                {t("hero.overline")}
              </span>
              <h1
                data-testid="hero-title"
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("hero.title")}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
                {t("hero.subtitle")}
              </p>
              <div className="aspect-[16/9] w-full overflow-hidden rounded-sm border border-slate-200">
                <img
                  src="/images/hero.jpg"
                  alt={t("hero.title")}
                  className="w-full h-full object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </div>
            </div>
            <div id="newsletter" className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="rounded-sm border border-slate-200 bg-[#F8F9FA] p-8 lg:p-10 space-y-6">
                <div className="space-y-2">
                  <span className="text-[11px] tracking-[0.24em] uppercase text-[#C25934] font-semibold">
                    {t("ctaBanner.overline")}
                  </span>
                  <h2
                    className="text-2xl lg:text-3xl text-slate-900 tracking-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {t("hero.formTitle")}
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t("hero.formSubtitle")}
                  </p>
                </div>
                <NewsletterForm variant="hero" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4 space-y-4">
              <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
                {t("intro.overline")}
              </span>
              <h2
                className="text-3xl lg:text-4xl text-slate-900 tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("intro.title")}
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-slate-700 leading-relaxed">
              {introParagraphs.map((p, i) => (
                <p key={i} data-testid={`intro-p-${i}`}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
              {t("how.overline")}
            </span>
            <h2
              className="mt-4 text-3xl lg:text-4xl text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("how.title")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {howSteps.map((s, i) => (
              <div
                key={i}
                data-testid={`how-step-${i}`}
                className="border border-slate-200 rounded-sm p-6 bg-white space-y-3"
              >
                <div className="text-xs tracking-[0.2em] text-[#C25934] font-semibold">
                  {s.number}
                </div>
                <h3
                  className="text-lg text-slate-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {s.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 space-y-5 text-slate-700 leading-relaxed">
              {howParagraphs.map((p, i) => (
                <p key={i} data-testid={`how-p-${i}`}>{p}</p>
              ))}
            </div>
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-200">
                <img
                  src="/images/construction.jpg"
                  alt={t("how.title")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="py-24 bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
              {t("projects.overline")}
            </span>
            <h2
              className="mt-4 text-3xl lg:text-4xl text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("projects.title")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("projects.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectCards.map((c, i) => {
              const Icon = projectIcons[i % projectIcons.length];
              return (
                <div
                  key={i}
                  data-testid={`project-card-${i}`}
                  className="bg-white border border-slate-200 rounded-sm p-8 space-y-4"
                >
                  <Icon className="text-[#C25934]" size={26} />
                  <h3
                    className="text-xl text-slate-900"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {c.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {c.text}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-200">
              <img src="/images/project.jpg" alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-200">
              <img src="/images/building1.jpg" alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Value / About */}
      <section id="about" className="py-24 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
                {t("value.overline")}
              </span>
              <h2
                className="text-3xl lg:text-4xl text-slate-900 tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("value.title")}
              </h2>
              <p className="text-slate-700 leading-relaxed">{t("value.text")}</p>
              <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-200 mt-6">
                <img src="/images/building2.jpg" alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              {valueFeatures.map((f, i) => {
                const Icon = valueIcons[i % valueIcons.length];
                return (
                  <div
                    key={i}
                    data-testid={`value-feature-${i}`}
                    className="border border-slate-200 rounded-sm p-6 bg-[#F8F9FA] space-y-3"
                  >
                    <Icon className="text-[#C25934]" size={22} />
                    <h3 className="text-lg text-slate-900 font-medium">
                      {f.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {f.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="py-24 bg-slate-900 text-slate-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4 space-y-4">
            <span className="text-xs tracking-[0.24em] uppercase text-[#E39074] font-semibold">
              {t("transparency.overline")}
            </span>
            <h2
              className="text-3xl lg:text-4xl text-white tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("transparency.title")}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-slate-300 leading-relaxed">
            {transparencyParagraphs.map((p, i) => (
              <p key={i} data-testid={`transparency-p-${i}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Impact + Mission */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div className="bg-white border border-slate-200 rounded-sm p-10 space-y-4">
            <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
              {t("impact.overline")}
            </span>
            <h3
              className="text-2xl text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("impact.title")}
            </h3>
            <p className="text-slate-700 leading-relaxed">{t("impact.text")}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-10 space-y-4">
            <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
              {t("mission.overline")}
            </span>
            <h3
              className="text-2xl text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("mission.title")}
            </h3>
            <p className="text-slate-700 leading-relaxed">{t("mission.text")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white border-t border-slate-200">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="mb-12 text-center">
            <span className="text-xs tracking-[0.24em] uppercase text-[#C25934] font-semibold">
              {t("faq.overline")}
            </span>
            <h2
              className="mt-4 text-3xl lg:text-4xl text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("faq.title")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {faqItems.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group py-6"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6 text-left">
                  <span className="text-lg text-slate-900 font-medium">
                    {item.q}
                  </span>
                  <span className="text-[#C25934] text-xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-slate-900">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-4 text-slate-100">
              <span className="text-xs tracking-[0.24em] uppercase text-[#E39074] font-semibold">
                {t("ctaBanner.overline")}
              </span>
              <h2
                className="text-3xl lg:text-4xl text-white tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("ctaBanner.title")}
              </h2>
              <p className="text-slate-300 leading-relaxed">
                {t("ctaBanner.text")}
              </p>
            </div>
            <div className="lg:col-span-6 bg-white p-8 rounded-sm">
              <NewsletterForm variant="cta" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
